import { useCallback, useEffect, useState } from 'react';
import { TARGET_NETWORK } from '../config/network';
import debounce from 'lodash/debounce';

export function useNetwork() {
  const [isCorrectNetwork, setIsCorrectNetwork] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [account, setAccount] = useState<string | null>(null);

  const checkNetwork = useCallback(async () => {
    if (!window.ethereum) return false;

    try {
      const chainId = await window.ethereum.request({ method: 'eth_chainId' });
      return chainId === TARGET_NETWORK.chainId;
    } catch (error) {
      console.error('Error checking network:', error);
      return false;
    }
  }, []);

  const switchNetwork = useCallback(async () => {
    if (!window.ethereum) return false;

    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: TARGET_NETWORK.chainId }],
      });
      return true;
    } catch (switchError: any) {
      // This error code indicates that the chain has not been added to MetaMask.
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [TARGET_NETWORK],
          });
          return true;
        } catch (addError) {
          console.error('Error adding network:', addError);
          return false;
        }
      }
      console.error('Error switching network:', switchError);
      return false;
    }
  }, []);

  const connectWallet = useCallback(async () => {
    if (!window.ethereum) {
      alert('Please install MetaMask!');
      return false;
    }

    try {
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts',
      });
      setAccount(accounts[0]);
      setIsConnected(true);

      // Don't automatically switch network on connect
      const networkCorrect = await checkNetwork();
      setIsCorrectNetwork(networkCorrect);
      return true;
    } catch (error) {
      console.error('Error connecting wallet:', error);
      return false;
    }
  }, [checkNetwork]);

  // Create stable references to debounced setters
  const debouncedSetNetwork = useCallback(
    debounce((value: boolean) => {
      setIsCorrectNetwork(value);
    }, 100),
    []
  );

  const debouncedSetConnection = useCallback(
    debounce((value: boolean) => {
      setIsConnected(value);
    }, 100),
    []
  );

  useEffect(() => {
    let mounted = true;

    const handleAccountsChanged = (accounts: string[]) => {
      if (!mounted) return;
      if (accounts.length > 0) {
        setAccount(accounts[0]);
        debouncedSetConnection(true);
      } else {
        setAccount(null);
        debouncedSetConnection(false);
      }
    };

    const handleChainChanged = async () => {
      if (!mounted) return;
      const networkCorrect = await checkNetwork();
      debouncedSetNetwork(networkCorrect);
    };

    const init = async () => {
      if (!window.ethereum || !mounted) return;

      try {
        // Only check if already connected
        const accounts = await window.ethereum.request({
          method: 'eth_accounts',
        });

        if (mounted && accounts.length > 0) {
          setAccount(accounts[0]);
          debouncedSetConnection(true);
          // Check network but don't switch
          const networkCorrect = await checkNetwork();
          debouncedSetNetwork(networkCorrect);
        }

        // Set up event listeners
        const provider = window.ethereum;
        if (provider?.on) {
          provider.on('accountsChanged', handleAccountsChanged);
          provider.on('chainChanged', handleChainChanged);
        }
      } catch (error) {
        console.error('Error initializing wallet:', error);
      }
    };

    init();

    return () => {
      mounted = false;
      const provider = window.ethereum;
      if (provider?.removeListener) {
        provider.removeListener('accountsChanged', handleAccountsChanged);
        provider.removeListener('chainChanged', handleChainChanged);
      }
      debouncedSetNetwork.cancel();
      debouncedSetConnection.cancel();
    };
  }, [checkNetwork, debouncedSetConnection, debouncedSetNetwork]);

  return {
    isCorrectNetwork,
    isConnected,
    account,
    connectWallet,
    switchNetwork,
  };
}
